import { authenticatedMiddleware } from '../../../permissions/middlewares/authenticatedMiddleware.js';
import { publicOperationMiddleware } from '../../../permissions/middlewares/publicOperationMiddleware.js';
import { checkPermissionMiddleware } from '../../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../../permissions/roles.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { from, map, of, switchMap } from 'rxjs';
import { InterviewSlot } from '../entities/InterviewSlot.js';
import { generateInterviewSlots } from '../server/generateInterviewSlots.js';

const subscribeInterviewSlotsForSchool = registerSubscription({
  operationName: 'subscribeInterviewSlotsForSchool',
  /**
   * Must stay reachable without a session: the public SuS registration form calls this to show the
   * bookable interview slots before an account exists (`sg-sus-entry-form-registration-form.ts`).
   *
   * TODO it currently returns every slot of the school through a raw `watchQuery`, so it neither
   * applies the `InterviewSlot` publishFilter nor hides `childID` - which discloses which child is
   * booked at which time to anyone who knows a schoolID. The form only needs free slots plus the
   * one it selected, so narrow the query and drop `childID` from the public shape.
   */
  middlewares: [publicOperationMiddleware('Oeffentliches SuS-Anmeldeformular zeigt buchbare Termine')],
  type: () => [InterviewSlot],
  params: [{ type: () => ID, name: 'schoolID' }],
  resolve: (schoolID: string) => (gqlFields: BaseQLSelectionSet<InterviewSlot>) =>
    watchQuery<InterviewSlot>(
      InterviewSlot._collectionName,
      { schoolID: { $eq: schoolID } },
      { projection: gqlFields, sort: { startDate: 1 } },
    ),
});

const subscribeAllInterviewSlots = registerSubscription({
  operationName: 'subscribeAllInterviewSlots',
  middlewares: [authenticatedMiddleware],
  type: () => [InterviewSlot],
  params: [
    { type: () => [ID], name: 'schoolIDs' },
    { type: () => Boolean, name: 'showAll' },
  ],
  resolve: (schoolIDs?: string[], showAll?: boolean) => (gqlFields: BaseQLSelectionSet<InterviewSlot>) =>
    from(publishFilter(InterviewSlot._class)).pipe(
      // this is not paginated as we only will have maybe 100 slots at most per school and a pagination would be overkill for the ui
      switchMap(publishFilter => {
        const query: Record<string, any> = publishFilter ? { $and: [publishFilter] } : {};

        if (schoolIDs?.length) {
          // if the user asks for interviews on context give it to him
          query.schoolID = { $in: schoolIDs };
        } else if (showAll) {
          // if a user does not ask for interviews in a specific context we only give him all interviews if the showAll flag is set
          query.schoolID = { $exists: true };
        } else {
          // if a user does not ask for interviews in a specific context and the showAll flag is not set we do not give him any interviews
          return of([]);
        }
        return watchQuery<InterviewSlot>(InterviewSlot._collectionName, query, {
          sort: { startDate: 1 },
          projection: gqlFields,
        });
      }),
    ),
});

/**
 * @class
 * Options for generating interview slots
 */
@Entity()
export class GenerateSlotsForSchoolOptions extends AdornisEntity {
  static override _class = 'GenerateSlotsForSchoolOptions';

  constructor(doc: PartialEntityData<GenerateSlotsForSchoolOptions>) {
    super(doc);
  }

  @validate(sgNonOptional())
  @Field(() => ID)
  schoolID: Maybe<string>;

  @validate(sgNonOptional())
  @Field(() => Date)
  start: Maybe<Date>;

  @validate(sgNonOptional())
  @Field(() => Int)
  numberOfSlotsPerTimeSlot: Maybe<number>;

  @validate(sgNonOptional())
  @Field(() => Int)
  numberOfTimeslotsPerDay: Maybe<number>;

  @validate(sgNonOptional())
  @Field(() => Int)
  numberOfMinutesPerSlot: Maybe<number>;
}

const generateInterviewSlotsForSchool = registerMutation({
  operationName: 'generateInterviewSlotsForSchool',
  middlewares: [checkPermissionMiddleware(Permission.ADMINISTRATIVE_SUS_INTERVIEWS, ANY_CONTEXT)],
  type: () => Int,
  params: [{ type: () => GenerateSlotsForSchoolOptions, name: 'options' }],
  resolve: (options: GenerateSlotsForSchoolOptions) => () => generateInterviewSlots(options),
});

const subscribeInterviewSlotForChild = registerSubscription({
  operationName: 'subscribeInterviewSlotForChild',
  middlewares: [authenticatedMiddleware],
  type: () => InterviewSlot,
  params: [{ type: () => ID, name: 'childID' }],
  resolve: (childID: Maybe<string>) => (gqlFields: BaseQLSelectionSet<InterviewSlot>) => {
    return from(publishFilter(InterviewSlot._class)).pipe(
      switchMap(publishFilter => {
        if (!childID) return of(null);
        const query = publishFilter
          ? { $and: [publishFilter, { childID: { $eq: childID } }] }
          : { childID: { $eq: childID } };

        return watchQuery<InterviewSlot>(InterviewSlot._collectionName, query);
      }),
      map(slots => slots?.[0]),
    );
  },
});

export const InterviewAPI = {
  subscribePublicSlotsForSchool: subscribeInterviewSlotsForSchool,
  subscribeAll: subscribeAllInterviewSlots,
  generate: generateInterviewSlotsForSchool,
  subscribeForChild: subscribeInterviewSlotForChild,
};
